import { buildCustomFieldFiltersParam } from '../data-lists/customFieldFilterParam';

/** Pure parts of the list filters (types, value helpers) — no React Native, unit-tested. */

export type FilterOption = { value: string; label: string; color?: string };
export type DateRangeValue = { from?: string; to?: string; preset?: string };
export type RangeValue = { min?: number; max?: number };

export type FilterField =
  | { key: string; label: string; type: 'multi'; options: FilterOption[]; section?: string; hint?: string }
  | { key: string; label: string; type: 'choice'; options: FilterOption[]; section?: string; hint?: string }
  | { key: string; label: string; type: 'range'; unit?: string; section?: string; hint?: string }
  | { key: string; label: string; type: 'date'; section?: string; hint?: string }
  | { key: string; label: string; type: 'text'; placeholder?: string; section?: string; hint?: string }
  /** Records picked by search (vendors, customers, categories…) — stored with their labels. */
  | { key: string; label: string; type: 'entity'; search: (q: string) => Promise<FilterOption[]>; placeholder?: string; section?: string; hint?: string };

/** multi: string[] · choice: string · range: RangeValue · date: DateRangeValue · text: string · entity: FilterOption[] */
export type FilterValues = Record<string, any>;

export type SortOption = { key: string; label: string; sortBy: string; sortOrder: 'asc' | 'desc'; icon?: string };
export type QuickView = { key: string; label: string; filters: FilterValues; sort?: string; icon?: string };
export type SavedView = { id: string; name: string; filters: FilterValues; sort?: string; customFields?: Record<string, string[]>; search?: string };

export interface ListFilterState {
  filters: FilterValues;
  sort?: string;
  search: string;
  customFields: Record<string, string[]>;
  /** Quick view or saved view currently applied (cleared when the user edits filters). */
  view?: string;
}

export const EMPTY: ListFilterState = { filters: {}, search: '', customFields: {} };

export function isActive(field: FilterField | undefined, value: any): boolean {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (field?.type === 'range') return value.min !== undefined || value.max !== undefined;
  if (field?.type === 'date') return !!(value.from || value.to);
  return true;
}

export function countActive(fields: FilterField[], state: Pick<ListFilterState, 'filters' | 'customFields'>): number {
  return fields.filter((f) => isActive(f, state.filters[f.key])).length
    + Object.values(state.customFields || {}).filter((v) => v.length > 0).length;
}

/** "a – b" for ranges and dates; joined labels for lists. */
export function describeValue(field: FilterField, value: any, locale?: string): string {
  const fmtDate = (d?: string) => { if (!d) return '…'; try { return new Date(`${d}T00:00:00`).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return d; } };
  switch (field.type) {
    case 'multi': return (value as string[]).map((v) => field.options.find((o) => o.value === v)?.label || v).join(', ');
    case 'choice': return field.options.find((o) => o.value === value)?.label || String(value);
    case 'range': return `${value.min ?? '0'} – ${value.max ?? '∞'}${field.unit ? ` ${field.unit}` : ''}`;
    case 'date': return `${fmtDate(value.from)} – ${fmtDate(value.to)}`;
    case 'entity': return (value as FilterOption[]).map((o) => o.label).join(', ');
    default: return String(value);
  }
}

/** Query helpers for screens building their API request. */
export const fv = {
  list: (v: any): string[] | undefined => (Array.isArray(v) && v.length ? v.map((x: any) => (typeof x === 'object' ? x.value : String(x))) : undefined),
  csv: (v: any): string | undefined => fv.list(v)?.join(','),
  min: (v: RangeValue | undefined) => v?.min,
  max: (v: RangeValue | undefined) => v?.max,
  from: (v: DateRangeValue | undefined) => v?.from,
  to: (v: DateRangeValue | undefined) => v?.to,
  text: (v: any) => (typeof v === 'string' && v.trim() ? v.trim() : undefined),
  customFields: (v: Record<string, string[]>) => buildCustomFieldFiltersParam(v || {}),
};

const today = () => new Date();
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** Date ranges relative to today, for quick views ("today", "this week"). */
export const relativeRange = {
  today: (): DateRangeValue => ({ from: iso(today()), to: iso(today()) }),
  days: (n: number): DateRangeValue => { const d = today(); return { from: iso(new Date(d.getFullYear(), d.getMonth(), d.getDate() - n + 1)), to: iso(d) }; },
  thisWeek: (): DateRangeValue => { const d = today(); const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7)); return { from: iso(monday), to: iso(d) }; },
  thisMonth: (): DateRangeValue => { const d = today(); return { from: iso(new Date(d.getFullYear(), d.getMonth(), 1)), to: iso(d) }; },
};

